// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';
import debounce from 'lodash/debounce';

import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {baseComparator} from 'common/util';
import {formatDate} from 'common/util/date';
import {emitTrackEvent} from 'common/util/dom';
import {fuzzyMatch} from 'common/util/fuzzy-match';
import {
  formatProperty,
  FormattedProperty,
  FULL_DISPLAY_DATE_FORMAT,
  getFormattedPropertyName,
  getRawPropertyName,
  getRenderInputPropertyValue,
} from 'common/util/format-property';
import {profilePropertyClient} from 'common/report/queries/app-api/profile-property-client';
import {UserDemographicsTab} from 'common/report/util/profile/types';
import {TrackedEvents, TrackedProperties} from 'common/report/util/profile/tracking';
import {OrderBy} from 'common/util/array';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';

import type {PropertyModificationEvent} from './profile-editable-property';
import './profile-editable-property';
import {template} from './template';
import './index.styl';

const PROPERTY_PROPAGATION_INFO = `Changes may take a minute to process and display in your project.`;

export interface ProfileDemographicsState {
  hasShownInfoToast: boolean;
  isCreatingNewPropertyLoading: boolean;
  toggleState: UserDemographicsTab;
  newPropertyName: string;
  newPropertyValue: string;
  // for updating/deleting properties, we'll need to tell their components to render a spinner
  propertiesLoading: string[];
  // We need to cache properties we've created to show them at the top of the list
  propertiesCreatedInSession: FormattedProperty[];
  // and hide properties we've deleted
  propertiesDeletedInSession: string[];
  showPropertiesSearch: boolean;
  showCreateNewProperty: boolean;
  searchText: string;
  // checking user's write_profile_explore permission
  canWriteProfile: boolean;
}

export type FilteredProperty = {
  property: FormattedProperty;
  matchParts: {
    name: Nullable<string[]>;
    value: Nullable<string[]>;
  };
  score: number;
};

interface ProfileDemographicsJsxProps {
  attrs: ProfileDemographicsAttrs;
  on?: {
    updateProperty?: (ev: CustomEvent) => void;
  };
}

export interface ProfileDemographicsAttrs {
  'data-group-id'?: string;
  'data-group-name': string;
  'distinct-id': string;
  'profile-exists': boolean;
  'utc-offset': number;
  'sidebar-mode'?: boolean;
  timezone: string;
  'user-properties': FormattedProperty[];
  'user-properties-error': boolean;
  'user-properties-loading': boolean;
  'can-write-profile': boolean;
}

export class ProfileDemographics extends Component<
  ProfileDemographicsState,
  ProfileDemographicsAttrs,
  unknown,
  unknown,
  IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<ProfileDemographicsAttrs> {
    return {
      ...super.attrsSchema,
      'data-group-name': `string`,
      'data-group-id': `string`,
      'distinct-id': `string`,
      'profile-exists': `boolean`,
      'user-properties': `json`,
      'user-properties-loading': `boolean`,
      'user-properties-error': `boolean`,
      'utc-offset': `number`,
      'sidebar-mode': `boolean`,
      timezone: `string`,
      'can-write-profile': `boolean`,
    };
  }

  get config() {
    return {
      defaultState: {
        hasShownInfoToast: false,
        isCreatingNewPropertyLoading: false,
        toggleState: UserDemographicsTab.PropertiesTab,
        newPropertyName: ``,
        newPropertyValue: ``,
        propertiesLoading: [],
        propertiesCreatedInSession: [],
        propertiesDeletedInSession: [],
        showPropertiesSearch: false,
        showCreateNewProperty: false,
        searchText: ``,
        canWriteProfile: false,
      },
      template,
      contexts: [`i18n` as const],
      helpers: {
        getRawPropertyName,
        // TODO: tests fail if we use these in the jade template as $attr(``). Fix the tests to not have this behavior
        isPropertiesLoading: (): boolean => this.hasAttribute(`user-properties-loading`),
        hasDataGroupId: (): boolean => !!this.attr(`data-group-id`),
        handleCreatePropertyButton: (): void => {
          this.update({
            showCreateNewProperty: true,
          });
        },
        handleUpdateNewPropertyName: (ev): void => {
          this.update({
            newPropertyName: ev.target.value,
          });
        },
        handleUpdateNewPropertyValue: (ev): void => {
          this.update({
            newPropertyValue: ev.target.value,
          });
        },
        handleCancelNewProperty: (): void => {
          this.update({
            showCreateNewProperty: false,
          });
        },
        handleSaveNewProperty: async (): Promise<void> => {
          const {newPropertyName, newPropertyValue} = this.state;

          this.trackEvent(TrackedEvents.CreateProperty, {
            'Property Name': newPropertyName,
            'Property Value': newPropertyValue,
          });

          this.update({
            isCreatingNewPropertyLoading: true,
          });
          await this.setProperty(newPropertyName, newPropertyValue);
          this.showInfoToastIfNew(this.t(`profile.prop-created`));

          this.update({
            propertiesCreatedInSession: this.state.propertiesCreatedInSession.concat([
              formatProperty(newPropertyName, newPropertyValue, {
                dataGroupId: this.attr(`data-group-id`),
                timezone: this.attr(`timezone`),
                i18n: this.getContext(`i18n`),
              }),
            ]),
            newPropertyName: ``,
            newPropertyValue: ``,
            showCreateNewProperty: false,
            isCreatingNewPropertyLoading: false,
          });
        },
        handleUpdateInput: (ev): void => {
          this.debouncedTrackSearchInput();

          this.update({
            searchText: ev.target.value,
          });
        },
        handlePropertySearchToggle: (): void => {
          this.update({
            showPropertiesSearch: !this.state.showPropertiesSearch,
            searchText: ``,
          });
        },
        handleDemographicToggleSelect: (ev: CustomEvent<{selected: UserDemographicsTab}>): void => {
          this.update({
            toggleState: ev.detail.selected,
          });
        },
        handlePropertyUpdate: async ({
          detail: {propertyName, propertyValue},
        }: {
          detail: PropertyModificationEvent;
        }): Promise<void> => {
          this.addPropertyToLoading(propertyName);
          try {
            await this.setProperty(propertyName, propertyValue);
            this.removePropertyFromLoading(propertyName);

            this.showInfoToastIfNew(this.t(`profile.prop-updated`));

            this.dispatchEvent(
              new CustomEvent(`updateProperty`, {
                detail: {
                  propertyName,
                  propertyValue,
                },
              }),
            );
          } catch (error) {
            this.removePropertyFromLoading(propertyName);
            this.addErrorToast(error);
          }
        },
        handlePropertyDelete: async ({
          detail: {propertyName, propertyValue},
        }: {
          detail: PropertyModificationEvent;
        }): Promise<void> => {
          this.addPropertyToLoading(propertyName);

          try {
            await this.unsetProperty(propertyName);

            this.removePropertyFromLoading(propertyName);

            this.addToast(this.t(`profile.prop-deleted`, {propertyName}), () => {
              this.setProperty(propertyName, propertyValue);
              const {propertiesDeletedInSession} = this.state;

              const index = propertiesDeletedInSession.indexOf(propertyName);
              propertiesDeletedInSession.splice(index, 1);

              this.update({
                propertiesDeletedInSession,
              });
            });

            this.update({
              propertiesDeletedInSession: this.state.propertiesDeletedInSession.concat([propertyName]),
            });
          } catch (error) {
            this.removePropertyFromLoading(propertyName);
            this.addErrorToast(error);
          }
        },
        getToggleOptions: (): Obj[] => [
          {
            value: UserDemographicsTab.PropertiesTab,
            label: `${UserDemographicsTab.PropertiesTab} (${this.helpers.getAllDisplayProperties().length})`,
          },
        ],
        getFilteredProperties: (): FilteredProperty[] => {
          const {searchText, propertiesDeletedInSession, propertiesCreatedInSession} = this.state;

          const displayProperties = this.attr(`user-properties`)
            .concat(propertiesCreatedInSession)
            .filter((name) => !propertiesDeletedInSession.includes(getRawPropertyName(name)))
            .map((formattedProperty) => {
              // match on user-facing property names, rather than "hidden" underlying names
              const keyMatch = fuzzyMatch(getFormattedPropertyName(formattedProperty), searchText);
              const valueMatch = fuzzyMatch(String(getRenderInputPropertyValue(formattedProperty)), searchText);

              // Choose greater of the two if any (have to do the || 0 because Math.max returns NaN if undefined is ever passed)
              const score = Math.max(keyMatch?.score || 0, valueMatch?.score || 0);

              return {
                property: formattedProperty,
                matchParts: {
                  name: keyMatch?.matchParts,
                  value: valueMatch?.matchParts,
                },
                score,
              };
            })
            // Only display this property if there was at least one match
            .filter((prop) => prop.score);

          // Sorted by descending score, so the better matches come first
          return displayProperties.sort(
            baseComparator<{score: number}, number>({order: OrderBy.Descending, transform: (prop) => prop.score}),
          );
        },
        getAllDisplayProperties: (): FormattedProperty[] => {
          const {propertiesCreatedInSession, propertiesDeletedInSession} = this.state;

          const displayProperties = this.attr(`user-properties`).filter(
            (name) => !propertiesDeletedInSession.includes(getRawPropertyName(name)),
          );

          // Sort ignoring case (so AaBb instead of ABab)
          const comparator = baseComparator<FormattedProperty, string>({
            transform: (prop) => getFormattedPropertyName(prop).toLowerCase(),
          });
          displayProperties.sort(comparator);

          // Always show properties created this session in front, but also remove new properties that were deleted
          return propertiesCreatedInSession
            .filter((property) => !propertiesDeletedInSession.includes(getRawPropertyName(property)))
            .concat(displayProperties);
        },
        getPropertiesThatAreLoading: (): string[] => this.state.propertiesLoading,
        formatMessageDate: (dateString: string): string => {
          return formatDate(new Date(dateString), {
            unit: `full`,
            customFormatting: {
              full: FULL_DISPLAY_DATE_FORMAT,
            },
          });
        },
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  addPropertyToLoading(propertyName: string): void {
    this.update({
      propertiesLoading: this.state.propertiesLoading.concat(propertyName),
    });
  }

  removePropertyFromLoading(propertyName: string): void {
    const propertiesLoading = this.state.propertiesLoading;
    const index = propertiesLoading.indexOf(propertyName);
    propertiesLoading.splice(index, 1);

    this.update({
      propertiesLoading,
    });
  }

  // We only want to show these informational toasts once so users know there might be some small delay
  // but not bug them every time if they're doing a bunch of edits/creates
  showInfoToastIfNew(toastMessage: string): void {
    if (!this.state.hasShownInfoToast) {
      this.addToast(toastMessage);
      this.update({
        hasShownInfoToast: true,
      });
    }
  }

  addToast(propertyChangeMessage, undo?: () => void) {
    addToast({
      type: ToastType.SUCCESS,
      message: `${propertyChangeMessage} ${PROPERTY_PROPAGATION_INFO}`,
      undoable: !!undo,
      undoCallback: undo,
    });
  }

  addErrorToast(error) {
    addToast({
      type: ToastType.ERROR,
      message: error.message,
    });
  }

  debouncedTrackSearchInput = debounce(this.trackSearchInput, 500);

  trackSearchInput(): void {
    this.trackEvent(TrackedEvents.SearchUserProperties, {
      'Search query': this.state.searchText,
    });
  }

  trackEvent(eventName: TrackedEvents, props: TrackedProperties = {}): void {
    emitTrackEvent(this, {eventName, props});
  }

  async setProperty(name, value): Promise<any> {
    return profilePropertyClient.setProperties(
      {
        profileId: this.attr(`distinct-id`),
        properties: {[name]: value},
      },
      this.attr(`data-group-name`),
      this.attr(`utc-offset`),
    );
  }

  async unsetProperty(name): Promise<any> {
    return profilePropertyClient.deleteProfileProperties(
      {
        propertyNames: [name],
        profileId: this.attr(`distinct-id`),
      },
      this.attr(`data-group-name`),
    );
  }
}

export const ProfileDemographicsEl = defineAndMakeJsxFactory<ProfileDemographicsJsxProps>(
  `profile-demographics`,
  ProfileDemographics,
);
export const ProfileDemographicsReact = panel2react<ProfileDemographicsJsxProps>(
  `profile-demographics`,
  ProfileDemographics,
);
